import { OvFormInputReadOnly } from '@components'
import { KEYS, LABELS, TOOLTIPS } from '@constants'
import { formatValueToCurrency, roundNumber } from '@helpers/formatCentToAmount.helper'
import { filterAvailableVatCodes } from 'src/helpers/vat/filterVatCodes.helper'
import { useIsDesktop } from '@hooks/useIsDesktop'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { Add, Edit, Inventory } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import Grid2 from '@mui/material/Unstable_Grid2/Grid2'
import { ISingleCountry, ISingleDocument, ISingleProduct } from '@typings'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { OvFormInput } from 'src/components/core/ov-form-input/ov-form-input'
import { OvFormContext } from 'src/components/core/ov-form-provider/ov-form-provider'
import { OvForm } from 'src/components/core/ov-form/ov-form'
import { ProductPickerModal } from 'src/partials/modals/products/productPicker.modal'
import { DocumentLinesContext } from './lines/document-lines.context'
import { DocumentSingleLine } from './lines/document.singleLine'
import { InlineClientCreateForm } from './inline-client-create.form'
import { InlineClientUpdateForm } from './inline-client-update.form'

interface ITaskTableColumns {
  label: string
  key: string
  align: 'left' | 'right'
}

interface ICalculatedTotals {
  total: number
  totalExVat: number
  totalVat: number
}

export const UpdateInvoiceForm = observer(({ documentType }: { documentType: ISingleDocument['type'] }) => {
  const { client, company, invoice, quotation, shared, ui } = useStore()

  const [expanded, setExpanded] = useState<string | false>(KEYS.LINES)
  const [productPickerOpen, setProductPickerOpen] = useState(false)
  const [showInlineClientForm, setShowInlineClientForm] = useState(false)
  const [showInlineClientEditForm, setShowInlineClientEditForm] = useState(false)
  const form = useFormContext()
  const isMobile = useIsMobile()
  const isDesktop = useIsDesktop()
  const formContext = useContext(OvFormContext)
  const store = useMemo(() => (documentType === 'invoice' ? invoice : quotation), [documentType])
  const isInvoice = useMemo(() => store.single.type === 'invoice' || documentType === 'invoice', [store.single.type])
  const margin = useMemo(() => {
    if (isMobile) return '0'
    return ui.menuOpen ? '320px' : '64px'
  }, [ui.menuOpen, isMobile])
  const FormInput = OvFormInput<ISingleDocument>
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    keyName: '_id',
    name: 'lines',
  })

  const {
    formState: { defaultValues },
    setValue,
  } = useFormContext<ISingleDocument>()

  const length = useWatch({
    name: 'invoice_expires_after',
    control: form.control,
    defaultValue: 30,
  })
  const start = useWatch({
    name: 'issued_at',
    control: form.control,
    defaultValue: new Date(),
  })

  const getDefaultExpiredValue = useMemo(() => {
    let date = dayjs().add(30, 'days').toDate()
    if (start && length) date = dayjs(start).add(length, 'days').toDate()
    // Don't auto-set expires_at - let it remain empty for draft invoices
    // On finalization, backend will calculate it if empty, or honor user's custom date if set
    return date
  }, [length, start])

  const getCurrentCountryName = useCallback(
    (id: ISingleCountry['id'] | undefined) => {
      const countryName = shared.countries && shared.countries.find(el => el.id === id)?.name

      if (!countryName) return ''
      return countryName
    },
    [shared.countries]
  )

  // Watch all form lines to calculate totals
  const formLines = useWatch({
    control: form.control,
    name: 'lines',
    defaultValue: [],
  })

  // Watch selected client to filter VAT codes
  const selectedClientId = useWatch({
    control: form.control,
    name: 'client_id',
  })

  // Get selected client from store
  const selectedClient = useMemo(() => {
    return client.list.find(c => c.id === selectedClientId)
  }, [selectedClientId, client.list])

  // Filter available VAT codes based on company and client tax status
  const filteredVatCodes = useMemo(() => {
    return filterAvailableVatCodes(shared.current_vat_codes, company.current_company, selectedClient)
  }, [shared.current_vat_codes, company.current_company, selectedClient])

  // Helper to get VAT percentage
  const getVatPercentage = useCallback(
    (vatCodeId: number) => {
      return shared.current_vat_codes.find(({ id }) => id === vatCodeId)?.percentage || 0
    },
    [shared.current_vat_codes]
  )

  // Calculate totals from current form state
  const calculatedTotals = useMemo(() => {
    if (!formLines || formLines.length === 0) {
      return { total: 0, totalExVat: 0, totalVat: 0 }
    }

    return formLines.reduce(
      (acc: ICalculatedTotals, line: { quantity: number; price: number; vat_code_id: number }) => {
        const qty = Number(line.quantity) || 0
        const prc = Number(line.price) || 0
        const lineTotal = qty * prc
        const vatPercentage = getVatPercentage(line.vat_code_id)
        const lineVat = vatPercentage ? roundNumber(lineTotal * vatPercentage) / 100 : 0

        return {
          totalExVat: roundNumber(acc.totalExVat + lineTotal),
          totalVat: roundNumber(acc.totalVat + lineVat),
          total: roundNumber(acc.total + lineTotal + lineVat),
        }
      },
      { total: 0, totalExVat: 0, totalVat: 0 }
    )
  }, [formLines, getVatPercentage])

  // Calculate VAT breakdown by rate
  const vatBreakdown = useMemo(() => {
    const lines = formContext.readOnly ? store.single.lines : formLines
    if (!lines || lines.length === 0) {
      return []
    }

    // Group lines by VAT rate
    const breakdown = new Map<number, { rate: number; subtotal: number; tax: number }>()

    lines.forEach((line: any) => {
      const qty = Number(line.quantity) || 0
      const prc = Number(line.price) || 0
      const lineSubtotal = qty * prc
      const vatPercentage = formContext.readOnly
        ? (line.vat_code?.percentage ?? 0)
        : getVatPercentage(line.vat_code_id)
      const lineTax = vatPercentage ? roundNumber(lineSubtotal * vatPercentage) / 100 : 0

      if (!breakdown.has(vatPercentage)) {
        breakdown.set(vatPercentage, {
          rate: vatPercentage,
          subtotal: 0,
          tax: 0,
        })
      }

      const entry = breakdown.get(vatPercentage)!
      entry.subtotal = roundNumber(entry.subtotal + lineSubtotal)
      entry.tax = roundNumber(entry.tax + lineTax)
    })

    // Convert to array and sort by rate descending (21%, 9%, 0%)
    return Array.from(breakdown.values()).sort((a, b) => b.rate - a.rate)
  }, [formLines, formContext.readOnly, store.single.lines, getVatPercentage])

  const handleAccordion = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  // Check if company is tax payable
  const isCompanyTaxPayable = company.current_company?.tax_payable ?? true

  // Auto-select client if exactly 1 client exists and form is not in read-only mode
  useEffect(() => {
    if (!formContext.readOnly && client.list.length === 1) {
      const currentClientId = form.getValues('client_id')
      if (!currentClientId) {
        form.setValue('client_id', client.list[0].id, { shouldDirty: true })
      }
    }
  }, [client.list, formContext.readOnly])

  // Set default VAT code on the first line item based on company tax status
  useEffect(() => {
    if (!formContext.readOnly && filteredVatCodes.length > 0) {
      const currentLines = form.getValues('lines')
      if (currentLines && currentLines.length > 0 && !currentLines[0].vat_code_id) {
        form.setValue('lines.0.vat_code_id', filteredVatCodes[0].id, { shouldDirty: false })
      }
    }
  }, [filteredVatCodes, formContext.readOnly])

  // Set focus on the label field of the first line item on page load
  // A small delay is needed to allow the MUI accordion animation to complete
  // before the input is accessible for focus
  useEffect(() => {
    if (!formContext.readOnly) {
      const timer = setTimeout(() => {
        form.setFocus('lines.0.label')
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [formContext.readOnly])

  const taskTableColumns: ITaskTableColumns[] = [
    {
      label: 'Omschrijving',
      key: 'label',
      align: 'left',
    },
    {
      label: 'Aantal',
      key: 'quantity',
      align: 'left',
    },
    {
      label: 'Prijs',
      key: 'price',
      align: 'left',
    },
    ...(isCompanyTaxPayable
      ? [
          {
            label: 'BTW',
            key: 'percentage',
            align: 'left' as const,
          },
        ]
      : []),
    {
      label: 'Subtotaal',
      key: 'subtotal',
      align: 'left',
    },
    ...(!formContext.readOnly
      ? [
          {
            label: '',
            key: 'actions',
            align: 'left' as const,
          },
        ]
      : []),
  ]

  const handleAddRow = () => {
    const defaultVatCode = filteredVatCodes.length > 0 ? filteredVatCodes[0] : undefined
    append(
      {
        label: '',
        description: null,
        price: 0,
        quantity: 1,
        vat_code_id: defaultVatCode?.id ?? null,
        vat_code: defaultVatCode ?? null,
      },
      { shouldFocus: false }
    )
  }

  const handleAddNewClient = () => {
    setShowInlineClientForm(true)
  }

  const handleClientCreated = (newClient: { id: number }) => {
    // Auto-select the newly created client in the invoice form
    setValue('client_id', newClient.id, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setShowInlineClientForm(false)
    formContext.setExtraDirty?.(false)
  }

  const handleCancelInlineClientForm = () => {
    setShowInlineClientForm(false)
    formContext.setExtraDirty?.(false)
  }

  const handleInlineClientDirtyChange = (isDirty: boolean) => {
    formContext.setExtraDirty?.(isDirty)
  }

  const handleEditClient = () => {
    setShowInlineClientEditForm(true)
  }

  const handleClientUpdated = () => {
    // Refresh client list and close edit form
    client.getAll()
    setShowInlineClientEditForm(false)
    formContext.setExtraDirty?.(false)
  }

  const handleCancelInlineClientEditForm = () => {
    setShowInlineClientEditForm(false)
    formContext.setExtraDirty?.(false)
  }

  const handleInlineClientEditDirtyChange = (isDirty: boolean) => {
    formContext.setExtraDirty?.(isDirty)
  }

  const handleProductSelect = (product: ISingleProduct) => {
    // Get the default VAT code (first available VAT code from filtered list only)
    // For non-tax-payable companies, filteredVatCodes will be empty, so defaultVatCode will be undefined
    const defaultVatCode = filteredVatCodes.length > 0 ? filteredVatCodes[0] : undefined

    // Append a new line with product data
    // Only assign VAT code if company is tax payable
    append({
      label: product.name,
      description: product.description || null,
      price: product.unit_price || 0,
      quantity: 1,
      vat_code_id: company.current_company?.tax_payable
        ? (product.vat_code_id || defaultVatCode?.id)
        : null,
      vat_code: company.current_company?.tax_payable ? defaultVatCode : null,
    }, { shouldFocus: false })

    // Close the modal
    setProductPickerOpen(false)
  }

  const renderTableData = useMemo(() => {
    return (
      <TableContainer sx={{ height: 'auto' }}>
        <Table size="small">
          {isDesktop && (
            <TableHead>
              <TableRow>
                {taskTableColumns.map(singleColumn => (
                  <TableCell
                    key={singleColumn.key}
                    align={singleColumn.align}
                    sx={{
                      fontWeight: 600,
                      borderBottom: 2,
                      borderColor: 'divider',
                    }}>
                    <OvFormInputReadOnly label={singleColumn.label} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody
            sx={{
              '& .MuiTableRow-root:not(:last-child)': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
            }}>
            {isMobile && (
              <Accordion>
                <AccordionSummary
                  sx={{ pointerEvents: 'none', paddingX: 0 }}
                  expandIcon={
                    <ExpandMoreIcon
                      color="primary"
                      sx={{ opacity: 0, width: 0, display: 'none' }}
                    />
                  }>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    flex={1}>
                    <OvFormInputReadOnly label={LABELS.DESCRIPTION} />
                    <OvFormInputReadOnly label={LABELS.SUB_TOTAL} />
                  </Stack>
                </AccordionSummary>
              </Accordion>
            )}
            {fields?.map((field, index: number) => {
              return (
                <DocumentSingleLine
                  key={field._id}
                  index={index}
                  removeLine={remove}
                  filteredVatCodes={filteredVatCodes}
                  {...field}
                />
              )
            })}
          </TableBody>
        </Table>
        {!formContext.readOnly && (
          <Box
            display="flex"
            gap={1}
            mt={2}>
            <Tooltip title={TOOLTIPS.ADD_DOCUMENT_LINE}>
              <Button
                variant="text"
                onClick={handleAddRow}
                endIcon={<Add />}>
                Regel toevoegen
              </Button>
            </Tooltip>
            <Tooltip title={TOOLTIPS.SELECT_FROM_CATALOG}>
              <Button
                variant="text"
                onClick={() => setProductPickerOpen(true)}
                endIcon={<Inventory />}>
                {LABELS.SELECT_FROM_CATALOG}
              </Button>
            </Tooltip>
          </Box>
        )}
      </TableContainer>
    )
  }, [isMobile, isDesktop, formContext.readOnly, fields, remove, handleAddRow])

  return (
    <DocumentLinesContext>
      <>
        <OvForm>
          {company.current_company?.uses_kor && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>KOR (Kleine Ondernemingsregeling):</strong> Je bedrijf valt onder de Kleine
                Ondernemingsregeling. Je kunt alleen 0% BTW in rekening brengen.
              </Typography>
            </Alert>
          )}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {formContext.readOnly ? (
              <Card sx={{ p: 2 }}>
                <Stack
                  spacing={3}
                  direction="column">
                  <Box>
                    <Typography
                      variant="h4"
                      mb={2}>
                      Algemeen
                    </Typography>
                    <Grid2
                      container
                      spacing={2}>
                      <Grid2
                        xs={12}
                        md={6}>
                        <Stack spacing={2}>
                          <FormInput
                            name="client.name"
                            label={LABELS.CLIENT}
                          />
                          {formContext.readOnly && (
                            <OvFormInputReadOnly label="Adress">
                              <List
                                dense
                                disablePadding>
                                <ListItemText primary={defaultValues?.client?.street} />
                                <ListItemText primary={defaultValues?.client?.postal_code} />
                                <ListItemText primary={defaultValues?.client?.city} />
                                <ListItemText primary={getCurrentCountryName(defaultValues?.client?.country_id)} />
                              </List>
                            </OvFormInputReadOnly>
                          )}
                        </Stack>
                      </Grid2>
                      <Grid2
                        xs={12}
                        md={6}>
                        <Stack spacing={2}>
                          <FormInput
                            name="issued_at"
                            type="date"
                            label={LABELS.DATE}
                            defaultValue={new Date()}
                          />
                          {isInvoice && !store.single.document_number && invoice.concept_number ? (
                            <OvFormInputReadOnly
                              label={LABELS.INVOICE_NUMBER}
                              value={`${invoice.concept_number} (concept)`}
                            />
                          ) : (
                            <FormInput
                              readOnly={isInvoice}
                              name={isInvoice ? 'document_number' : 'number'}
                              label={isInvoice ? LABELS.INVOICE_NUMBER : LABELS.QUOTATION_REFERENCE}
                            />
                          )}
                        </Stack>
                      </Grid2>
                      <Grid2 xs={12}>
                        <FormInput
                          name="subject"
                          label={LABELS.SUBJECT}
                        />
                      </Grid2>
                    </Grid2>
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      mb={2}>
                      Regels
                    </Typography>
                    {renderTableData}
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      mb={2}>
                      Voorwaarden
                    </Typography>
                    <Grid2
                      container
                      spacing={2}>
                      <Grid2
                        xs={12}
                        md={6}>
                        <FormInput
                          type="date"
                          name="expires_at"
                          label={LABELS.EXPIRES_AFTER}
                          defaultValue={getDefaultExpiredValue}
                        />
                      </Grid2>
                    </Grid2>
                  </Box>
                </Stack>
              </Card>
            ) : (
              <>
                <Accordion
                  expanded
                  onChange={handleAccordion(KEYS.GENERAL)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
                    <Typography variant="h4">Algemeen</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid2
                      container
                      spacing={2}>
                      <Grid2
                        xs={12}
                        md={6}>
                        <FormInput
                          name="issued_at"
                          type="date"
                          label={LABELS.DATE}
                          disablePast
                        />
                      </Grid2>
                      <Grid2
                        xs={12}
                        md={6}>
                        {isInvoice && !store.single.document_number && invoice.concept_number ? (
                          <OvFormInputReadOnly
                            label={LABELS.INVOICE_NUMBER}
                            value={`${invoice.concept_number} (concept)`}
                          />
                        ) : (
                          <FormInput
                            readOnly={isInvoice}
                            name={isInvoice ? 'document_number' : 'number'}
                            label={isInvoice ? LABELS.INVOICE_NUMBER : LABELS.QUOTATION_REFERENCE}
                          />
                        )}
                      </Grid2>
                      <Grid2 xs={12}>
                        <Stack
                          spacing={2}
                          divider={<Divider />}>
                          <Stack
                            spacing={1}
                            direction="column">
                            <FormInput
                              name="client_id"
                              type="select"
                              label={LABELS.CLIENT}
                              options={client.list}
                              valueKey="id"
                              labelKey="name"
                              output="value"
                            />
                            {!formContext.readOnly && (
                              <Stack
                                direction="row"
                                spacing={2}>
                                {form.getValues('client_id') && !showInlineClientForm && !showInlineClientEditForm && (
                                  <Tooltip title={TOOLTIPS.EDIT_CLIENT}>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={handleEditClient}
                                      endIcon={<Edit />}>
                                      {LABELS.CLIENT_EDIT}
                                    </Button>
                                  </Tooltip>
                                )}
                                {!showInlineClientForm && !showInlineClientEditForm && (
                                  <Tooltip title={TOOLTIPS.ADD_NEW_CLIENT_INLINE}>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={handleAddNewClient}
                                      endIcon={<Add />}>
                                      {LABELS.CLIENT_CREATE}
                                    </Button>
                                  </Tooltip>
                                )}
                              </Stack>
                            )}
                          {showInlineClientForm && (
                            <InlineClientCreateForm
                              onClientCreated={handleClientCreated}
                              onCancel={handleCancelInlineClientForm}
                              onDirtyChange={handleInlineClientDirtyChange}
                            />
                          )}
                          {showInlineClientEditForm && form.getValues('client_id') && (
                            <InlineClientUpdateForm
                              clientId={form.getValues('client_id')}
                              onClientUpdated={handleClientUpdated}
                              onCancel={handleCancelInlineClientEditForm}
                              onDirtyChange={handleInlineClientEditDirtyChange}
                            />
                          )}
                          </Stack>
                          <FormInput
                            name="subject"
                            label={LABELS.SUBJECT}
                          />
                        </Stack>
                      </Grid2>
                      <Grid2 xs={12}></Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  expanded
                  onChange={handleAccordion(KEYS.LINES)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
                    <Typography variant="h4">Regels</Typography>
                  </AccordionSummary>
                  <AccordionDetails>{renderTableData}</AccordionDetails>
                </Accordion>
                <Accordion
                  expanded
                  onChange={handleAccordion(KEYS.TERMS)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
                    <Typography variant="h4">Voorwaarden</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid2
                      container
                      spacing={2}>
                      <Grid2
                        xs={12}
                        md={6}>
                        <FormInput
                          type="date"
                          name="expires_at"
                          disablePast
                          label={LABELS.EXPIRES_AFTER}
                          value={getDefaultExpiredValue}
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </>
            )}
          </div>
        </OvForm>
        <Card
          sx={{
            left: margin,
            right: 0,
            mt: 2,
            zIndex: 1,
            transition: 'left 0.25s',
          }}>
          <CardContent>
            <Grid2
              container
              spacing={2}>
              <Grid2 xs={isCompanyTaxPayable ? 4 : 12}>
                <Typography>Subtotaal</Typography>
                <Typography
                  color="primary"
                  fontWeight="bold">
                  {formatValueToCurrency(formContext.readOnly ? store.single.subtotal : calculatedTotals.totalExVat)}
                </Typography>
              </Grid2>
              {isCompanyTaxPayable && (
                <>
                  <Grid2 xs={4}>
                    {vatBreakdown.length > 0 ? (
                      <Stack spacing={1}>
                        {vatBreakdown.map((breakdown) => (
                          <div key={breakdown.rate}>
                            <Typography>BTW {breakdown.rate}%</Typography>
                            <Typography
                              color="primary"
                              fontWeight="bold">
                              {formatValueToCurrency(breakdown.tax)}
                            </Typography>
                          </div>
                        ))}
                      </Stack>
                    ) : (
                      <>
                        <Typography>BTW</Typography>
                        <Typography
                          color="primary"
                          fontWeight="bold">
                          {formatValueToCurrency(formContext.readOnly ? store.single.vat : calculatedTotals.totalVat)}
                        </Typography>
                      </>
                    )}
                  </Grid2>
                  <Grid2 xs={4}>
                    <Typography>Totaal</Typography>
                    <Typography
                      color="primary"
                      fontWeight="bold">
                      {formatValueToCurrency(formContext.readOnly ? store.single.total : calculatedTotals.total)}
                    </Typography>
                  </Grid2>
                </>
              )}
            </Grid2>
          </CardContent>
        </Card>

        <ProductPickerModal
          open={productPickerOpen}
          onClose={() => setProductPickerOpen(false)}
          onSelect={handleProductSelect}
        />
      </>
    </DocumentLinesContext>
  )
})
